import React, { Fragment, useState, Suspense, useRef } from 'react';
import { Formik } from 'formik';
import { filter, findIndex, map } from 'lodash';
import { CrewTypesList } from './list';
import { Scalars, CrewType, SaveCrewTypeSelectionPayload } from '@bechtel/types';
import {
  Box,
  Text,
  ButtonAppearance,
  Spinner,
  SpinnerSize,
  ToastType,
  ToastAction,
  useToastsDispatch,
  theme,
} from '@bechtel/gds';
import {
  ImportUsersContainerStyled,
  ImportUsersInnerBlockStyled,
  ImportUsersSubContainerStyled,
  FooterButtonStyled,
  ImportUsersFooterButtonStyled,
  ImportUserHeadingStyled,
} from './styled';
import { GET_PROJECT_CREW_TYPES, SAVE_CREW_TYPE_SELECTION } from '@bechtel/client-gql';
import { get } from 'lodash';
import { NetworkStatus, useQuery, useMutation } from '@apollo/client';
import {
  useAppUser,
  ErrorRetry,
  SuspenseLoader,
  ErrorBoundary,
  ProjectAppTypes,
  useEnvProvider,
} from '@bechtel/client-common';
import { UpdateCrewTypesRequestError } from './error/types';
import UpdateCrewTypesRequestErrorModal from '~/user-admin/import/error';
import RemoveCrewTypesModal from '~/user-admin/import/confirm-modal';
import { t, Trans } from '@lingui/macro';

export interface FormikState {
  crewTypes: CrewType[];
}

const CrewTypeUpdateToast = () => {
  return (
    <Box p={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
      <Text family="graphik" weight="600" size="500">
        <Trans>Crew Types saved successfully.</Trans>
      </Text>
    </Box>
  );
};

let initialValues: FormikState | undefined = undefined;

export const ImportUsers = () => {
  const { envConfig } = useEnvProvider();
  const { user } = useAppUser();
  const dispatchToast = useToastsDispatch();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const crewTypesToRemove = useRef<CrewType[]>([]);
  const crewTypesToUpdate = useRef<CrewType[]>([]);

  const [updateCrewTypesRequestError, setUpdateCrewTypesRequestError] = useState<UpdateCrewTypesRequestError | null>();
  const { loading, error, data, refetch, networkStatus } = useQuery(GET_PROJECT_CREW_TYPES, {
    notifyOnNetworkStatusChange: true,
    variables: {
      id: user?.project?.id,
    },
  });
  const isPFAppType = envConfig.appName === ProjectAppTypes.PF;
  const crewTypesArr: CrewType[] = get(data, 'projectConfigById.crewTypes', []);
  initialValues = {
    crewTypes: crewTypesArr,
  };
  const activeCount = filter(crewTypesArr, (inner) => inner.applicableToProject).length;

  const [updateProjectCrewTypes, { loading: removeCrewTypesLoading, error: updateError }] = useMutation<
    { saveCrewTypeSelection: SaveCrewTypeSelectionPayload },
    { input: Scalars['ID'][] }
  >(SAVE_CREW_TYPE_SELECTION, {
    //@ts-ignore saveCrewTypeSelection will be there on data
    update: (cache, { data: { saveCrewTypeSelection } }) => {
      if (!saveCrewTypeSelection || updateError) return;

      const queryResult = cache.readQuery<{ projectConfigById: Record<string, string> }>({
        query: GET_PROJECT_CREW_TYPES,
        variables: {
          id: user?.project?.id,
        },
      });

      if (queryResult) {
        cache.writeQuery({
          query: GET_PROJECT_CREW_TYPES,
          data: {
            projectConfigById: {
              ...queryResult.projectConfigById,
              crewTypes: map(get(queryResult, 'projectConfigById.crewTypes', []), (ct) => {
                const index = findIndex(
                  saveCrewTypeSelection.crewTypes,
                  (inner: Record<string, string>) => inner.id == ct.id
                );
                if (index != -1) return { ...ct, applicableToProject: true };
                else return { ...ct, applicableToProject: false };
              }),
            },
          },
        });
      }
    },
    onCompleted: (data: { saveCrewTypeSelection: SaveCrewTypeSelectionPayload }) => {
      const updateCrewTypeRequestError = get(data, 'saveCrewTypeSelection.error', false);
      if (updateCrewTypeRequestError) {
        setUpdateCrewTypesRequestError(updateCrewTypeRequestError);
      } else {
        const toastAction: ToastAction = {
          type: 'ADD_TOAST',
          payload: {
            toastType: ToastType.INFO,
            content: CrewTypeUpdateToast,
            autoDismissTimeout: 5000,
          },
        };
        dispatchToast(toastAction);
      }
    },
    onError: (error) => {
      setUpdateCrewTypesRequestError(error);
    },
  });
  const onConfirm = () => {
    updateProjectCrewTypes({
      variables: { input: map(crewTypesToUpdate.current, 'id') },
    });
    setShowDeleteConfirm(false);
  };
  const closeModal = () => {
    setUpdateCrewTypesRequestError(null);
  };
  return (
    <Fragment>
      <ImportUsersContainerStyled>
        {(loading || networkStatus === NetworkStatus.refetch) && (
          <Box
            flexGrow={{ sm: 1 }}
            alignItems={{ sm: 'center' }}
            flexDirection={{ sm: 'column' }}
            justifyContent={{ sm: 'center' }}
            data-testid="loading"
          >
            <Spinner size={SpinnerSize.LARGE} />
          </Box>
        )}
        {!loading && error && (
          <div data-testid="error-retry">
            <ErrorRetry refetch={refetch}>
              <Trans>There was a problem loading Crew Types list. Please click the button below to try again.</Trans>
            </ErrorRetry>
          </div>
        )}

        {initialValues && !loading && !error && (
          <Formik
            enableReinitialize={true}
            initialValues={initialValues}
            onSubmit={async (values) => {
              const activeCrewTypes = filter(initialValues?.crewTypes, (inner) => inner.applicableToProject);
              const itemsToRemove: CrewType[] = [];
              activeCrewTypes.forEach((item) => {
                const presentStateItemIndex = findIndex(values.crewTypes, (inner) => inner.id == item.id);
                if (!values.crewTypes[presentStateItemIndex].applicableToProject) {
                  itemsToRemove.push(values.crewTypes[presentStateItemIndex]);
                }
              });
              crewTypesToRemove.current = itemsToRemove;
              const finalActiveCrewTypes = filter(values.crewTypes, (inner) => inner.applicableToProject);
              crewTypesToUpdate.current = finalActiveCrewTypes;
              if (itemsToRemove.length > 0) setShowDeleteConfirm(true);
              else {
                updateProjectCrewTypes({
                  variables: { input: map(finalActiveCrewTypes, 'id') },
                });
              }
            }}
          >
            {(form) => {
              const { dirty, isValid } = form;
              return (
                <>
                  <ImportUsersSubContainerStyled flexDirection={{ sm: 'column' }}>
                    <ImportUserHeadingStyled>
                      <Box mb={{ sm: '550' }} flexDirection={{ sm: 'column' }}>
                        <Box mb={{ sm: '450' }}>
                          <Text family="graphik" size="500" weight="600">
                            <Trans>Crew Types</Trans> ({activeCount} <Trans>Selected</Trans>)
                          </Text>
                        </Box>

                        <Text
                          family="graphik"
                          size="300"
                          color="grey.1000"
                          style={{ lineHeight: `${theme.spacings[550]}` }}
                        >
                          {t`Edit settings for which users will automatically be activated in ${
                            isPFAppType ? 'PipeFighter' : 'ArcStryker'
                          }.`}
                        </Text>
                      </Box>
                    </ImportUserHeadingStyled>
                    <ImportUsersInnerBlockStyled>
                      <CrewTypesList form={form} />
                      {!loading && !error && crewTypesArr.length == 0 && (
                        <Box mt={{ sm: '450' }} ml={{ sm: '500' }}>
                          <Text data-testid="retry">
                            <Trans>No Crew Types found.</Trans>
                          </Text>
                        </Box>
                      )}
                    </ImportUsersInnerBlockStyled>
                  </ImportUsersSubContainerStyled>
                  {!loading && !error && (
                    <FooterButtonStyled>
                      <Box>
                        <Box mr={{ sm: '400' }}>
                          <ImportUsersFooterButtonStyled
                            disabled={removeCrewTypesLoading || !dirty}
                            onClick={() => {
                              form.handleReset();
                            }}
                            appearance={ButtonAppearance.SECONDARY}
                          >
                            <Trans>Discard Changes</Trans>
                          </ImportUsersFooterButtonStyled>
                        </Box>
                        <ImportUsersFooterButtonStyled
                          onClick={() => {
                            form.handleSubmit();
                          }}
                          disabled={removeCrewTypesLoading || !dirty || !isValid}
                          appearance={ButtonAppearance.SECONDARY}
                        >
                          {removeCrewTypesLoading && <Spinner />}
                          {!removeCrewTypesLoading && t`Save Changes`}
                        </ImportUsersFooterButtonStyled>
                      </Box>
                    </FooterButtonStyled>
                  )}
                </>
              );
            }}
          </Formik>
        )}
      </ImportUsersContainerStyled>
      {showDeleteConfirm && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <RemoveCrewTypesModal
              crewTypesToRemove={crewTypesToRemove.current}
              onCancel={() => {
                setShowDeleteConfirm(false);
              }}
              onConfirm={onConfirm}
            />
          </Suspense>
        </ErrorBoundary>
      )}
      {updateCrewTypesRequestError && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <UpdateCrewTypesRequestErrorModal updateCrewTypesError={updateCrewTypesRequestError} close={closeModal} />
          </Suspense>
        </ErrorBoundary>
      )}
    </Fragment>
  );
};
